import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { AuthImage, AuthVideo } from "@/components/AuthImage";
import { useAuth } from "@/context/AuthContext";
import { Send, DollarSign, MessageSquare, CheckCircle2, Star, ArrowLeft, Tag, Calendar, Clock, BookmarkPlus, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function IssueDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [issue, setIssue] = useState(null);
  const [bids, setBids] = useState([]);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatTarget, setChatTarget] = useState(null);
  const [bidDialogOpen, setBidDialogOpen] = useState(false);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        api.get(`/issues/${id}`),
        api.get(`/issues/${id}/bids`),
      ]);
      setIssue(a.data);
      setBids(b.data);
      try {
        const bk = await api.get(`/bookings/by-issue/${id}`);
        setBooking(bk.data);
      } catch (_) { setBooking(null); }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403 && detail && typeof detail === "object" && detail.upgrade_required) {
        toast.info(detail.message || "Upgrade to Premium to see this job.");
        navigate("/upgrade");
        return;
      }
      toast.error("Failed to load");
      navigate(-1);
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // Auto-open the chat panel when arriving via the Messages page
  // (e.g. /issue/<id>?chat=<peer_user_id>). Once handled we strip the query
  // param so refresh doesn't re-open the sheet unexpectedly.
  useEffect(() => {
    if (loading || !issue) return;
    const peer = searchParams.get("chat");
    if (peer && !chatOpen) {
      setChatTarget(peer);
      setChatOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("chat");
      setSearchParams(next, { replace: true });
    }
  }, [loading, issue, searchParams, setSearchParams, chatOpen]);

  const isOwner = user && issue && user.user_id === issue.owner_id;
  const isShop = user?.role === "shop";
  const acceptedBid = bids.find((b) => b.status === "accepted");

  const acceptBid = async (bid_id) => {
    if (!window.confirm("Accept this quote? Other quotes will be declined.")) return;
    try {
      await api.post(`/bids/${bid_id}/accept`);
      toast.success("Quote accepted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const markCompleted = async () => {
    if (!window.confirm("Mark this job completed?")) return;
    try {
      await api.post(`/issues/${id}/complete`);
      toast.success("Job completed");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const openChat = (targetId) => {
    setChatTarget(targetId);
    setChatOpen(true);
  };

  if (loading) return <div className="min-h-screen bg-[#09090B]"><Navbar /><div className="p-12 text-zinc-500">Loading...</div></div>;
  if (!issue) return null;

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-10">
        <button onClick={() => navigate(-1)} className="btn-ghost flex items-center gap-2 mb-6" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            {/* Media gallery */}
            {issue.file_paths?.length > 0 ? (
              <div className="border border-white/10 bg-[#121214] p-4 mb-6">
                <div className="label-eyebrow mb-3">EVIDENCE</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {issue.file_paths.map((p) => (
                    <div key={p} className="aspect-video bg-zinc-900 overflow-hidden">
                      {p.toLowerCase().match(/\.(mp4|mov|webm|avi)$/) ? (
                        <AuthVideo path={p} className="w-full h-full" />
                      ) : (
                        <AuthImage path={p} className="w-full h-full object-cover" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="border border-white/10 bg-[#121214] p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-3">
                <StatusPill status={issue.status} />
                {issue.ai_category && (
                  <span className="text-xs uppercase tracking-widest border border-[#007AFF]/40 text-[#007AFF] px-2 py-1 flex items-center gap-1"><Tag className="w-3 h-3" />{issue.ai_category}</span>
                )}
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-3">{issue.title}</h1>
              <div className="text-sm text-zinc-500 mb-6">
                {issue.vehicle_make} {issue.vehicle_model} · {issue.vehicle_year || "—"} · {issue.location || "N/A"}
              </div>
              <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{issue.description}</p>

              {isOwner && issue.status === "in_progress" && (
                <div className="mt-6 pt-6 border-t border-white/10 flex flex-wrap gap-3">
                  <button onClick={markCompleted} className="btn-primary flex items-center gap-2" data-testid="complete-job-btn">
                    <CheckCircle2 className="w-4 h-4" /> Mark Completed
                  </button>
                </div>
              )}

              {isOwner && issue.status === "open" && (
                <div className="mt-6 pt-6 border-t border-white/10 flex flex-wrap gap-3">
                  <button
                    onClick={() => setEditDialogOpen(true)}
                    className="btn-secondary flex items-center gap-2"
                    data-testid="edit-issue-btn"
                  >
                    <FileText className="w-4 h-4" /> Edit issue
                  </button>
                  <button
                    onClick={() => setDeleteConfirmOpen(true)}
                    className="btn-ghost flex items-center gap-2 text-zinc-400 hover:text-[#FF3B30]"
                    data-testid="delete-issue-btn"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
              )}

              {isOwner && issue.status === "completed" && issue.accepted_shop_id && (
                <div className="mt-6 pt-6 border-t border-white/10">
                  <button onClick={() => setReviewDialogOpen(true)} className="btn-secondary flex items-center gap-2" data-testid="rate-shop-btn">
                    <Star className="w-4 h-4" /> Rate Shop
                  </button>
                </div>
              )}
            </div>
          </div>

          <aside className="lg:col-span-5 space-y-6">
            {/* Quotes panel */}
            <div className="border border-white/10 bg-[#121214]">
              <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                <div className="label-eyebrow">QUOTES</div>
                <span className="text-sm text-zinc-500 font-mono">{bids.length}</span>
              </div>

              {isShop && issue.status === "open" && bids.length === 0 && (
                <div className="p-6 border-b border-white/10">
                  <button onClick={() => setBidDialogOpen(true)} className="btn-primary w-full" data-testid="submit-bid-btn">
                    <DollarSign className="w-4 h-4 inline -mt-0.5 mr-1" /> Send Quote
                  </button>
                </div>
              )}

              {bids.length === 0 ? (
                <div className="p-8 text-center text-zinc-500 text-sm">No quotes yet.</div>
              ) : (
                <div className="divide-y divide-white/10">
                  {bids.map((b) => (
                    <div key={b.bid_id} className={`p-5 ${b.status === "accepted" ? "bg-[#34C759]/5" : ""}`} data-testid={`bid-${b.bid_id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="font-heading text-base font-bold">{b.shop_name}</div>
                          <div className="flex items-center gap-1 text-xs text-zinc-500">
                            <Star className="w-3 h-3 text-[#FF9500] fill-[#FF9500]" />
                            {b.shop_rating || "—"} ({b.shop_rating_count || 0})
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-heading text-2xl font-black text-[#FF3B30]">${Number(b.amount).toFixed(2)}</div>
                          <div className="label-eyebrow text-zinc-500">{formatEstimate(b)} · valid {b.valid_days || 7}D</div>
                        </div>
                      </div>
                      <p className="text-sm text-zinc-400 mb-3">{b.message}</p>
                      {b.line_items?.length > 0 && <QuoteBreakdown bid={b} />}
                      <div className="flex gap-2">
                        {isOwner && issue.status === "open" && (
                          <button onClick={() => acceptBid(b.bid_id)} className="btn-primary text-xs py-2" data-testid={`accept-bid-${b.bid_id}`}>
                            Accept
                          </button>
                        )}
                        {isOwner && (
                          <button onClick={() => openChat(b.shop_id)} className="btn-secondary text-xs py-2 flex items-center gap-1.5" data-testid={`chat-bid-${b.bid_id}`}>
                            <MessageSquare className="w-3 h-3" /> Chat
                          </button>
                        )}
                        {b.status === "accepted" && <span className="text-xs uppercase tracking-widest bg-[#34C759] text-black px-2 py-1">ACCEPTED</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Booking panel — shown once a quote is accepted */}
            {acceptedBid && (isOwner || (isShop && acceptedBid.shop_id === user.user_id)) && (
              <BookingPanel
                booking={booking}
                acceptedBid={acceptedBid}
                isOwner={isOwner}
                isShop={isShop}
                onBookClick={() => setBookingDialogOpen(true)}
                onChange={load}
              />
            )}

            {/* Shop action: message owner (if accepted) */}
            {isShop && acceptedBid && acceptedBid.shop_id === user.user_id && issue.owner_id && (
              <div className="border border-white/10 bg-[#121214] p-6">
                <button onClick={() => openChat(issue.owner_id)} className="btn-secondary w-full flex items-center justify-center gap-2" data-testid="message-owner-btn">
                  <MessageSquare className="w-4 h-4" /> Message Owner
                </button>
              </div>
            )}
          </aside>
        </div>
      </main>

      {/* Quote Dialog */}
      <BidDialog open={bidDialogOpen} onOpenChange={setBidDialogOpen} issueId={issue.issue_id} onSuccess={load} />

      {/* Booking Dialog */}
      {acceptedBid && (
        <BookingDialog
          open={bookingDialogOpen}
          onOpenChange={setBookingDialogOpen}
          acceptedBid={acceptedBid}
          onSuccess={load}
        />
      )}

      {/* Review Dialog */}
      <ReviewDialog
        open={reviewDialogOpen}
        onOpenChange={setReviewDialogOpen}
        shopId={issue.accepted_shop_id}
        issueId={issue.issue_id}
        onSuccess={load}
      />

      {/* Chat Sheet */}
      <Sheet open={chatOpen} onOpenChange={setChatOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md bg-[#09090B] border-l border-white/10 p-0 text-white">
          <SheetHeader className="p-6 border-b border-white/10">
            <SheetTitle className="font-heading">Private Chat</SheetTitle>
          </SheetHeader>
          {chatTarget && <ChatPanel targetId={chatTarget} issueId={issue.issue_id} />}
        </SheetContent>
      </Sheet>

      {/* Edit issue dialog */}
      <EditIssueDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        issue={issue}
        pendingBidCount={bids.filter((b) => b.status === "pending").length}
        onSuccess={load}
      />

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="bg-[#121214] border-white/10 text-white rounded-none max-w-md" data-testid="delete-issue-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Delete this issue?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <p className="text-zinc-300">
              This removes the post permanently. Shops can no longer see it.
            </p>
            {bids.filter((b) => b.status === "pending").length > 0 && (
              <div className="border border-[#FF9500]/40 bg-[#FF9500]/10 text-[#FF9500] p-3 text-xs uppercase tracking-widest" data-testid="delete-warning-quotes">
                ⚠ {bids.filter((b) => b.status === "pending").length} pending quote{bids.filter((b) => b.status === "pending").length === 1 ? "" : "s"} will be voided and the shop{bids.filter((b) => b.status === "pending").length === 1 ? "" : "s"} notified.
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 flex-col-reverse sm:flex-row">
            <button
              type="button"
              onClick={() => setDeleteConfirmOpen(false)}
              disabled={deleting}
              className="btn-secondary"
              data-testid="delete-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={async () => {
                setDeleting(true);
                try {
                  await api.delete(`/issues/${id}`);
                  toast.success("Issue deleted");
                  navigate("/owner");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Couldn't delete");
                } finally {
                  setDeleting(false);
                }
              }}
              disabled={deleting}
              className="btn-primary bg-[#FF3B30] hover:bg-[#FF3B30]/90"
              data-testid="delete-confirm-btn"
            >
              {deleting ? "Deleting…" : "Delete permanently"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <div className="label-eyebrow">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    open: "bg-[#FF3B30] text-white",
    in_progress: "bg-[#FF9500] text-black",
    completed: "bg-[#34C759] text-black",
    closed: "bg-zinc-700 text-white",
  };
  return <span className={`text-xs uppercase tracking-widest px-2 py-1 ${map[status] || map.open}`}>{status.replace("_", " ")}</span>;
}

function QuoteBreakdown({ bid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 mb-3" data-testid={`quote-breakdown-${bid.bid_id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3 py-2 text-xs uppercase tracking-widest text-zinc-400 hover:bg-white/5">
        <span>{open ? "Hide" : "View"} itemized breakdown</span>
        <span className="font-mono">{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 text-xs space-y-2">
          {bid.line_items.map((li, idx) => (
            <div key={idx} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-b-0">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 ${li.type === "labor" ? "bg-[#007AFF]/20 text-[#7eb6ff]" : li.type === "parts" ? "bg-[#FF9500]/20 text-[#ffc169]" : "bg-zinc-700 text-zinc-300"}`}>{li.type}</span>
                  <span className="text-zinc-300 truncate">{li.description}</span>
                </div>
                {li.qty > 1 && <div className="text-[10px] text-zinc-500 mt-0.5">{li.qty} × ${Number(li.unit_price).toFixed(2)}</div>}
              </div>
              <div className="font-mono text-zinc-300">${(Number(li.qty) * Number(li.unit_price)).toFixed(2)}</div>
            </div>
          ))}
          <div className="pt-2 space-y-1 text-zinc-500">
            {bid.parts_subtotal > 0 && <LineRow k="Parts" v={`$${bid.parts_subtotal.toFixed(2)}`} />}
            {bid.labor_subtotal > 0 && <LineRow k="Labor" v={`$${bid.labor_subtotal.toFixed(2)}`} />}
            {bid.fees_subtotal > 0 && <LineRow k="Fees" v={`$${bid.fees_subtotal.toFixed(2)}`} />}
            {bid.tax_amount > 0 && <LineRow k={`Tax (${bid.tax_rate}%)`} v={`$${bid.tax_amount.toFixed(2)}`} />}
            <div className="flex items-center justify-between pt-1.5 border-t border-white/10 text-white">
              <span className="label-eyebrow text-white">TOTAL</span>
              <span className="font-mono font-bold">${Number(bid.amount).toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LineRow({ k, v }) { return <div className="flex items-center justify-between"><span>{k}</span><span className="font-mono">{v}</span></div>; }

function BidDialog({ open, onOpenChange, issueId, onSuccess }) {
  const [items, setItems] = useState([{ id: 1, type: "labor", description: "", qty: 1, unit_price: "" }]);
  const [taxRate, setTaxRate] = useState("");
  const [timeValue, setTimeValue] = useState("");
  const [timeUnit, setTimeUnit] = useState("hours");
  const [validDays, setValidDays] = useState("7");
  const [message, setMessage] = useState("");
  const [proposedSlots, setProposedSlots] = useState([""]); // datetime-local strings
  const [loading, setLoading] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const reset = () => {
    setItems([{ id: 1, type: "labor", description: "", qty: 1, unit_price: "" }]);
    setTaxRate(""); setTimeValue(""); setTimeUnit("hours"); setValidDays("7"); setMessage("");
    setProposedSlots([""]);
  };

  const loadTemplates = async () => {
    try {
      const res = await api.get("/shop/quote-templates");
      setTemplates(res.data);
    } catch {
      setTemplates([]);
    }
  };

  const openTemplates = async () => {
    await loadTemplates();
    setTemplatesOpen(true);
  };

  const applyTemplate = (t) => {
    setItems((t.line_items || []).map((li, idx) => ({
      id: Date.now() + idx,
      type: li.type,
      description: li.description,
      qty: li.qty,
      unit_price: li.unit_price,
    })));
    setTaxRate(t.tax_rate ? String(t.tax_rate) : "");
    setTimeValue(t.estimated_time != null ? String(t.estimated_time) : "");
    setTimeUnit(t.estimated_time_unit || "hours");
    setValidDays(String(t.valid_days || 7));
    setMessage(t.message || "");
    setTemplatesOpen(false);
    toast.success(`Applied "${t.name}"`);
  };

  const deleteTemplate = async (id, name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      await api.delete(`/shop/quote-templates/${id}`);
      setTemplates((arr) => arr.filter((t) => t.template_id !== id));
      toast.success("Template deleted");
    } catch {
      toast.error("Couldn't delete template");
    }
  };

  const saveAsTemplate = async () => {
    const name = templateName.trim();
    if (!name) { toast.error("Name your template"); return; }
    const cleanedItems = items.filter((i) => i.description.trim() && Number(i.unit_price) > 0).map((i) => ({
      type: i.type, description: i.description.trim(), qty: Number(i.qty) || 1, unit_price: Number(i.unit_price),
    }));
    if (cleanedItems.length === 0) { toast.error("Add at least one line item before saving"); return; }
    setSavingTemplate(true);
    try {
      await api.post("/shop/quote-templates", {
        name,
        line_items: cleanedItems,
        estimated_time: Number(timeValue) || null,
        estimated_time_unit: timeUnit,
        tax_rate: Number(taxRate) || 0,
        valid_days: parseInt(validDays) || 7,
        message,
      });
      toast.success(`Saved "${name}" — use it on your next quote`);
      setSaveTemplateOpen(false);
      setTemplateName("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const addItem = (type = "labor") => setItems((arr) => [...arr, { id: Date.now(), type, description: "", qty: 1, unit_price: "" }]);
  const removeItem = (id) => setItems((arr) => arr.filter((i) => i.id !== id));
  const updateItem = (id, field, value) => setItems((arr) => arr.map((i) => i.id === id ? { ...i, [field]: value } : i));

  const partsSub = items.filter((i) => i.type === "parts").reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.unit_price) || 0), 0);
  const laborSub = items.filter((i) => i.type === "labor").reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.unit_price) || 0), 0);
  const feesSub = items.filter((i) => i.type === "fee").reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.unit_price) || 0), 0);
  const subtotal = partsSub + laborSub + feesSub;
  const taxAmount = subtotal * ((Number(taxRate) || 0) / 100);
  const total = subtotal + taxAmount;

  // Build the cleaned line items + slots once so both the confirm preview and
  // the actual POST use the exact same payload.
  const cleanedItems = items.filter((i) => i.description.trim() && Number(i.unit_price) > 0).map((i) => ({
    type: i.type, description: i.description.trim(), qty: Number(i.qty) || 1, unit_price: Number(i.unit_price),
  }));

  // Step 1: validate inputs, then OPEN the "double-check" confirm dialog.
  // Step 2 (confirmAndSend) actually fires the POST.
  const requestConfirm = () => {
    if (cleanedItems.length === 0) { toast.error("Add at least one line item"); return; }
    if (!timeValue || !message) { toast.error("Time estimate and message required"); return; }
    const tVal = Number(timeValue);
    if (tVal <= 0) { toast.error("Time estimate must be positive"); return; }
    setConfirmOpen(true);
  };

  const confirmAndSend = async () => {
    const tVal = Number(timeValue);
    const slotMinutes = 60; // for proposed slot end time only; shop schedule will dictate real slot length
    const cleanedSlots = proposedSlots
      .filter((s) => s)
      .map((s) => {
        const startDt = new Date(s);
        const endDt = new Date(startDt.getTime() + slotMinutes * 60 * 1000);
        return { start: startDt.toISOString(), end: endDt.toISOString() };
      });
    setLoading(true);
    try {
      await api.post("/bids", {
        issue_id: issueId,
        amount: Number(total.toFixed(2)),
        estimated_time: tVal,
        estimated_time_unit: timeUnit,
        estimated_days: timeUnit === "hours" ? Math.max(1, Math.ceil(tVal / 8)) : Math.max(1, Math.ceil(tVal)),
        message,
        line_items: cleanedItems,
        parts_subtotal: Number(partsSub.toFixed(2)),
        labor_subtotal: Number(laborSub.toFixed(2)),
        fees_subtotal: Number(feesSub.toFixed(2)),
        tax_rate: Number(taxRate) || 0,
        tax_amount: Number(taxAmount.toFixed(2)),
        valid_days: parseInt(validDays) || 7,
        proposed_slots: cleanedSlots,
      });
      toast.success("Quote sent");
      setConfirmOpen(false);
      onOpenChange(false);
      reset();
      onSuccess();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403 && detail && typeof detail === "object" && detail.upgrade_required) {
        toast.info(detail.message || "Upgrade to Premium to quote on more jobs.");
        window.location.href = "/upgrade";
        return;
      }
      toast.error((typeof detail === "string" && detail) || "Failed");
    }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121214] border-white/10 text-white rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Send Private Quote</DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-end -mt-2 mb-1">
          <button
            type="button"
            onClick={openTemplates}
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-400 hover:text-[#FF3B30] transition-colors"
            data-testid="use-template-btn"
          >
            <FileText className="w-3.5 h-3.5" /> Use template
          </button>
        </div>

        <div className="space-y-5">
          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="label-eyebrow">LINE ITEMS</label>
              <div className="hidden sm:flex gap-2">
                <button type="button" onClick={() => addItem("parts")} className="text-[11px] uppercase tracking-widest px-3 py-1.5 border border-white/15 hover:border-[#FF3B30] hover:text-[#FF3B30] transition" data-testid="add-parts-btn">+ Parts</button>
                <button type="button" onClick={() => addItem("labor")} className="text-[11px] uppercase tracking-widest px-3 py-1.5 border border-white/15 hover:border-[#FF3B30] hover:text-[#FF3B30] transition" data-testid="add-labor-btn">+ Labor</button>
                <button type="button" onClick={() => addItem("fee")} className="text-[11px] uppercase tracking-widest px-3 py-1.5 border border-white/15 hover:border-[#FF3B30] hover:text-[#FF3B30] transition" data-testid="add-fee-btn">+ Fee</button>
              </div>
            </div>
            <div className="space-y-3 sm:space-y-2">
              {items.map((it, idx) => (
                <div key={it.id} className="border border-white/10 bg-black/30 p-3 sm:p-0 sm:bg-transparent sm:border-0 sm:grid sm:grid-cols-12 sm:gap-2 sm:items-center" data-testid={`line-item-${idx}`}>
                  {/* MOBILE: each input gets its OWN full-width row so nothing gets squashed by `input-base`'s w-full.
                      DESKTOP (sm+): collapse back into a single 12-col grid via sm:contents. */}
                  <div className="sm:contents">
                    <select value={it.type} onChange={(e) => updateItem(it.id, "type", e.target.value)} className="input-base sm:col-span-2 py-2 text-xs uppercase mb-2 sm:mb-0" data-testid={`item-type-${idx}`}>
                      <option value="parts">Parts</option>
                      <option value="labor">Labor</option>
                      <option value="fee">Fee</option>
                    </select>
                    <input value={it.description} onChange={(e) => updateItem(it.id, "description", e.target.value)} placeholder={it.type === "parts" ? "Brake pads (front)" : it.type === "labor" ? "R&R brake pads" : "Disposal fee"} className="input-base sm:col-span-5 py-2 text-sm mb-2 sm:mb-0" data-testid={`item-desc-${idx}`} />
                  </div>
                  {/* qty + unit-$ pair — on mobile we put them side-by-side with explicit labels so the input is reasonably wide and the user can read what they typed. */}
                  <div className="grid grid-cols-2 gap-2 mb-2 sm:mb-0 sm:contents">
                    <div className="sm:col-span-1 flex flex-col">
                      <span className="sm:hidden text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Qty</span>
                      <input type="number" inputMode="decimal" min="1" value={it.qty} onChange={(e) => updateItem(it.id, "qty", e.target.value)} placeholder="1" className="input-base py-2 text-sm text-center" data-testid={`item-qty-${idx}`} />
                    </div>
                    <div className="sm:col-span-2 flex flex-col">
                      <span className="sm:hidden text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Unit $</span>
                      <input type="number" inputMode="decimal" step="0.01" min="0" value={it.unit_price} onChange={(e) => updateItem(it.id, "unit_price", e.target.value)} placeholder="0.00" className="input-base py-2 text-sm" data-testid={`item-price-${idx}`} />
                    </div>
                  </div>
                  {/* subtotal + remove — flow into grid on desktop, dedicated row on mobile */}
                  <div className="flex items-center justify-between gap-2 sm:contents">
                    <div className="sm:col-span-1 text-right font-mono text-sm sm:text-xs text-zinc-300 sm:text-zinc-400 whitespace-nowrap" data-testid={`item-subtotal-${idx}`}>
                      <span className="sm:hidden text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Line total:</span>
                      ${((Number(it.qty) || 0) * (Number(it.unit_price) || 0)).toFixed(2)}
                    </div>
                    <button type="button" onClick={() => removeItem(it.id)} disabled={items.length === 1} className="sm:col-span-1 text-zinc-500 hover:text-[#FF3B30] disabled:opacity-30 text-lg sm:text-base px-2" data-testid={`remove-item-${idx}`}>✕</button>
                  </div>
                </div>
              ))}
            </div>
            {/* Prominent "Add another item" footer — so shops can list multiple
                parts / labor / fees without hunting for the smaller buttons
                up top. Especially important on mobile. */}
            <div className="mt-3 grid grid-cols-3 gap-2 sm:flex sm:gap-3" data-testid="add-item-footer">
              <button type="button" onClick={() => addItem("parts")} className="flex items-center justify-center gap-1.5 py-2.5 border border-dashed border-white/20 hover:border-[#FF3B30] hover:text-[#FF3B30] text-xs uppercase tracking-widest transition" data-testid="add-parts-footer-btn">
                <span className="text-base leading-none">+</span> Parts
              </button>
              <button type="button" onClick={() => addItem("labor")} className="flex items-center justify-center gap-1.5 py-2.5 border border-dashed border-white/20 hover:border-[#FF3B30] hover:text-[#FF3B30] text-xs uppercase tracking-widest transition" data-testid="add-labor-footer-btn">
                <span className="text-base leading-none">+</span> Labor
              </button>
              <button type="button" onClick={() => addItem("fee")} className="flex items-center justify-center gap-1.5 py-2.5 border border-dashed border-white/20 hover:border-[#FF3B30] hover:text-[#FF3B30] text-xs uppercase tracking-widest transition" data-testid="add-fee-footer-btn">
                <span className="text-base leading-none">+</span> Fee
              </button>
            </div>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2 text-center sm:text-left" data-testid="add-item-hint">
              Tip: add a row for each part, labor task, or fee so the owner can see the full breakdown.
            </p>
          </div>

          {/* Totals */}
          <div className="border border-white/10 bg-[#09090B] p-4 space-y-1.5 text-sm">
            <LineRow k={`Parts subtotal`} v={`$${partsSub.toFixed(2)}`} />
            <LineRow k={`Labor subtotal`} v={`$${laborSub.toFixed(2)}`} />
            {feesSub > 0 && <LineRow k={`Fees`} v={`$${feesSub.toFixed(2)}`} />}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-zinc-400">Tax rate</span>
                <input type="number" step="0.01" min="0" max="20" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} placeholder="0" className="input-base w-20 py-1 text-xs" data-testid="tax-rate-input" />
                <span className="text-zinc-500">%</span>
              </div>
              <span className="font-mono text-zinc-400">${taxAmount.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-white/10">
              <span className="label-eyebrow text-white">TOTAL</span>
              <span className="font-heading text-2xl font-black text-[#FF3B30]" data-testid="quote-total">${total.toFixed(2)}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow block mb-2">TIME ESTIMATE *</label>
              <div className="grid grid-cols-[1fr_7rem] gap-2">
                <input type="number" inputMode="decimal" step="0.5" min="0" value={timeValue} onChange={(e) => setTimeValue(e.target.value)} className="input-base" placeholder="4" data-testid="bid-time-input" />
                <select value={timeUnit} onChange={(e) => setTimeUnit(e.target.value)} className="input-base" data-testid="bid-time-unit">
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </div>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">QUOTE VALID (DAYS)</label>
              <input type="number" inputMode="numeric" value={validDays} onChange={(e) => setValidDays(e.target.value)} className="input-base" data-testid="bid-valid-input" />
            </div>
          </div>

          <div>
            <label className="label-eyebrow block mb-2">MESSAGE TO OWNER *</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="input-base" placeholder="Scope, warranty, anything you want them to know..." data-testid="bid-message-input" />
          </div>

          {/* Optional proposed slots */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label-eyebrow">PROPOSED START TIMES (OPTIONAL)</label>
              <button type="button" onClick={() => setProposedSlots((p) => [...p, ""])} className="btn-ghost text-[10px] uppercase tracking-widest" data-testid="add-slot-btn">+ Slot</button>
            </div>
            <p className="text-[11px] text-zinc-500 mb-2">Suggest specific dates/times to override your weekly schedule for this job.</p>
            <div className="space-y-2">
              {proposedSlots.map((s, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`proposed-slot-${idx}`}>
                  <input
                    type="datetime-local"
                    value={s}
                    onChange={(e) => setProposedSlots((arr) => arr.map((v, i) => i === idx ? e.target.value : v))}
                    className="input-base flex-1 text-sm"
                  />
                  {proposedSlots.length > 1 && (
                    <button type="button" onClick={() => setProposedSlots((arr) => arr.filter((_, i) => i !== idx))} className="text-zinc-600 hover:text-[#FF3B30]" data-testid={`remove-slot-${idx}`}>✕</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <button
            type="button"
            onClick={() => setSaveTemplateOpen(true)}
            className="btn-secondary inline-flex items-center gap-1.5"
            data-testid="save-as-template-btn"
          >
            <BookmarkPlus className="w-4 h-4" /> Save as template
          </button>
          <button onClick={requestConfirm} disabled={loading} className="btn-primary" data-testid="bid-confirm-btn">{loading ? "..." : "Send Quote"}</button>
        </DialogFooter>
      </DialogContent>

      {/* "Double-check before sending" confirm dialog. */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-[#121214] border-white/10 text-white rounded-none max-w-md" data-testid="confirm-quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Double-check before sending</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-zinc-400">
              The owner will see this quote privately. Once sent, you can't edit it — only retract it.
            </p>
            <div className="border border-white/10 bg-black/30 p-4 space-y-2 text-sm">
              <div className="flex items-baseline justify-between border-b border-white/5 pb-2">
                <span className="text-zinc-400">Line items</span>
                <span className="font-mono text-white" data-testid="confirm-items-count">{cleanedItems.length}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-zinc-400">Subtotal</span>
                <span className="font-mono text-zinc-200">${subtotal.toFixed(2)}</span>
              </div>
              {taxAmount > 0 && (
                <div className="flex items-baseline justify-between">
                  <span className="text-zinc-400">Tax ({Number(taxRate) || 0}%)</span>
                  <span className="font-mono text-zinc-200">${taxAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex items-baseline justify-between border-t border-white/10 pt-2">
                <span className="text-white font-semibold">Total quote</span>
                <span className="font-mono text-[#FF3B30] text-lg font-bold" data-testid="confirm-total">${total.toFixed(2)}</span>
              </div>
              <div className="flex items-baseline justify-between text-xs text-zinc-500 pt-1">
                <span>Time estimate</span>
                <span>{timeValue} {timeUnit}</span>
              </div>
              <div className="flex items-baseline justify-between text-xs text-zinc-500">
                <span>Valid for</span>
                <span>{validDays} days</span>
              </div>
            </div>
            <p className="text-xs text-zinc-500 italic">
              Tip: a clear scope + warranty in your message gives owners confidence to pick your shop.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-2 flex-col-reverse sm:flex-row">
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              disabled={loading}
              className="btn-secondary"
              data-testid="confirm-cancel-btn"
            >
              Keep editing
            </button>
            <button
              type="button"
              onClick={confirmAndSend}
              disabled={loading}
              className="btn-primary"
              data-testid="confirm-send-btn"
            >
              {loading ? "Sending…" : "Looks good — send it"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Templates picker */}
      <Dialog open={templatesOpen} onOpenChange={setTemplatesOpen}>
        <DialogContent className="bg-[#121214] border-white/10 text-white rounded-none max-w-md" data-testid="templates-picker-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Your quote templates</DialogTitle>
          </DialogHeader>
          {templates.length === 0 ? (
            <div className="border border-dashed border-white/10 p-8 text-center text-zinc-500 text-sm">
              No templates yet. Build a quote below, then hit <span className="text-white">Save as template</span> to reuse it.
            </div>
          ) : (
            <ul className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="templates-list">
              {templates.map((t) => {
                const total = (t.line_items || []).reduce((s, li) => s + (Number(li.qty) || 0) * (Number(li.unit_price) || 0), 0);
                return (
                  <li key={t.template_id} className="border border-white/10 bg-[#09090B] p-3 flex items-center gap-3" data-testid={`template-row-${t.template_id}`}>
                    <button
                      type="button"
                      onClick={() => applyTemplate(t)}
                      className="flex-1 text-left min-w-0"
                      data-testid={`apply-template-${t.template_id}`}
                    >
                      <div className="text-sm font-semibold truncate">{t.name}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5 truncate font-mono">
                        {(t.line_items || []).length} item{(t.line_items || []).length === 1 ? "" : "s"}
                        {" · "}${total.toFixed(2)}
                        {t.estimated_time != null ? ` · ${t.estimated_time} ${t.estimated_time_unit || "hours"}` : ""}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteTemplate(t.template_id, t.name)}
                      className="text-zinc-600 hover:text-[#FF3B30] p-1.5"
                      title={`Delete ${t.name}`}
                      data-testid={`delete-template-${t.template_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      {/* Save-as-template prompt */}
      <Dialog open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen}>
        <DialogContent className="bg-[#121214] border-white/10 text-white rounded-none max-w-md" data-testid="save-template-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Save this quote as a template</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-zinc-500">We'll store the line items, time estimate, tax rate, validity, and message. Reuse it on any future quote with one click.</p>
            <input
              autoFocus
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="e.g. Standard brake job (front)"
              className="input-base"
              maxLength={80}
              data-testid="template-name-input"
              onKeyDown={(e) => { if (e.key === "Enter") saveAsTemplate(); }}
            />
          </div>
          <DialogFooter>
            <button onClick={saveAsTemplate} disabled={savingTemplate} className="btn-primary" data-testid="save-template-confirm">
              {savingTemplate ? "Saving..." : "Save template"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

function ReviewDialog({ open, onOpenChange, shopId, issueId, onSuccess }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!comment) { toast.error("Add a comment"); return; }
    setLoading(true);
    try {
      await api.post("/reviews", { shop_id: shopId, issue_id: issueId, rating, comment });
      toast.success("Thanks for the review");
      onOpenChange(false);
      onSuccess();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121214] border-white/10 text-white rounded-none">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Rate this shop</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-1">
            {[1,2,3,4,5].map((n) => (
              <button key={n} onClick={() => setRating(n)} className="p-1" data-testid={`star-${n}`}>
                <Star className={`w-7 h-7 ${n <= rating ? "text-[#FF9500] fill-[#FF9500]" : "text-zinc-700"}`} />
              </button>
            ))}
          </div>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} className="input-base" placeholder="How did they do?" data-testid="review-comment-input" />
        </div>
        <DialogFooter>
          <button onClick={submit} disabled={loading} className="btn-primary" data-testid="review-submit-btn">{loading ? "..." : "Submit Review"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChatPanel({ targetId, issueId }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [threadId, setThreadId] = useState(null);
  const bottomRef = useRef(null);
  const wsRef = useRef(null);
  const { user } = useAuth();

  const loadThread = useCallback(async () => {
    if (!user) return;
    const ids = [user.user_id, targetId].sort();
    const tid = issueId ? `${issueId}|${ids[0]}|${ids[1]}` : `${ids[0]}|${ids[1]}`;
    setThreadId(tid);
    try {
      const res = await api.get(`/messages/${encodeURIComponent(tid)}`);
      setMessages(res.data);
    } catch {}
  }, [user, targetId, issueId]);

  useEffect(() => {
    loadThread();
    // WS
    const token = localStorage.getItem("auth_token");
    if (token) {
      const wsUrl = `${BACKEND_URL.replace(/^http/, "ws")}/api/ws/${token}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "message" && msg.data) {
            // only include relevant
            setMessages((prev) => {
              if (prev.some((p) => p.message_id === msg.data.message_id)) return prev;
              if (msg.data.thread_id === threadId) return [...prev, msg.data];
              return prev;
            });
          }
        } catch {}
      };
      return () => { try { ws.close(); } catch {} };
    }
  }, [loadThread, threadId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim()) return;
    try {
      const res = await api.post("/messages", { recipient_id: targetId, issue_id: issueId, text });
      setMessages((prev) => [...prev, res.data]);
      setText("");
    } catch (e) { toast.error("Send failed"); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-80px)]">
      <div className="flex-1 overflow-y-auto p-6 space-y-3">
        {messages.length === 0 && <div className="text-center text-zinc-600 text-sm">No messages yet — start the conversation.</div>}
        {messages.map((m) => {
          const mine = m.sender_id === user?.user_id;
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.message_id}`}>
              <div className={`max-w-[80%] px-4 py-2.5 text-sm ${mine ? "bg-[#FF3B30] text-white" : "bg-[#121214] border border-white/10 text-white"}`}>
                {m.text}
                <div className="text-[10px] mt-1 opacity-60 font-mono">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <div className="p-4 border-t border-white/10 flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          className="input-base flex-1"
          placeholder="Write a message..."
          data-testid="chat-input"
        />
        <button onClick={send} className="btn-primary" data-testid="chat-send-btn"><Send className="w-4 h-4" /></button>
      </div>
    </div>
  );
}


function formatEstimate(b) {
  if (b.estimated_time && b.estimated_time_unit) {
    const v = Number(b.estimated_time);
    const unit = b.estimated_time_unit === "hours" ? (v === 1 ? "hr" : "hrs") : (v === 1 ? "day" : "days");
    return `${v % 1 === 0 ? v : v.toFixed(1)} ${unit}`;
  }
  return `${b.estimated_days || 1}D`;
}

function BookingPanel({ booking, acceptedBid, isOwner, isShop, onBookClick, onChange }) {
  const confirm = async () => {
    try { await api.post(`/bookings/${booking.booking_id}/confirm`); toast.success("Booking confirmed"); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel this appointment?")) return;
    try { await api.post(`/bookings/${booking.booking_id}/cancel`); toast.success("Booking cancelled"); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="border border-white/10 bg-[#121214]" data-testid="booking-panel">
      <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
        <div className="label-eyebrow flex items-center gap-2"><Calendar className="w-3 h-3 text-[#FF9500]" /> APPOINTMENT</div>
        {booking?.status && <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${booking.status === "confirmed" ? "bg-[#34C759] text-black" : booking.status === "pending" ? "bg-[#FF9500] text-black" : "bg-zinc-700 text-white"}`}>{booking.status}</span>}
      </div>
      <div className="p-6 space-y-4">
        {!booking ? (
          isOwner ? (
            <>
              <p className="text-sm text-zinc-400">Pick a time slot with this shop to lock in your appointment.</p>
              <button onClick={onBookClick} className="btn-primary w-full" data-testid="open-booking-dialog-btn">
                <Calendar className="w-4 h-4 inline -mt-0.5 mr-1" /> Book a slot
              </button>
            </>
          ) : (
            <p className="text-sm text-zinc-500">Waiting for the owner to book a slot.</p>
          )
        ) : (
          <>
            <div className="space-y-2 text-sm">
              <Row label="WHEN" value={new Date(booking.slot_start).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })} />
              <Row label="DURATION" value={`${booking.slot_minutes} min`} />
              {booking.notes && <Row label="NOTES" value={booking.notes} />}
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              {isShop && booking.status === "pending" && (
                <button onClick={confirm} className="btn-primary text-xs py-2" data-testid="confirm-booking-btn">Confirm</button>
              )}
              {(isOwner || isShop) && booking.status !== "cancelled" && (
                <button onClick={cancel} className="btn-secondary text-xs py-2" data-testid="cancel-booking-btn">Cancel</button>
              )}
              {isOwner && booking.status !== "cancelled" && (
                <button onClick={onBookClick} className="btn-ghost text-xs py-2" data-testid="rebook-btn">Reschedule</button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function BookingDialog({ open, onOpenChange, acceptedBid, onSuccess }) {
  const [slots, setSlots] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(null);
  const [notes, setNotes] = useState("");
  const [days, setDays] = useState(14);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/shops/${acceptedBid.shop_id}/availability`, { params: { days } })
      .then((res) => { setSlots(res.data.slots || []); setMeta(res.data); })
      .catch(() => toast.error("Failed to load availability"))
      .finally(() => setLoading(false));
  }, [open, acceptedBid.shop_id, days]);

  const proposed = acceptedBid.proposed_slots || [];

  const submit = async () => {
    if (!picked) { toast.error("Pick a time"); return; }
    setSubmitting(true);
    try {
      await api.post("/bookings", { bid_id: acceptedBid.bid_id, slot_start: picked, notes });
      toast.success("Booking requested");
      onOpenChange(false);
      setPicked(null); setNotes("");
      onSuccess();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  // Group available slots by date for cleaner UI
  const grouped = slots.reduce((acc, s) => {
    const d = new Date(s.start).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
    if (!acc[d]) acc[d] = [];
    acc[d].push(s);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121214] border-white/10 text-white rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Book your appointment</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {proposed.length > 0 && (
            <div>
              <div className="label-eyebrow mb-2 text-[#FF9500]"><Clock className="w-3 h-3 inline -mt-0.5 mr-1" /> SHOP-SUGGESTED TIMES</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {proposed.map((p, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setPicked(p.start)}
                    className={`text-left border px-3 py-2 text-sm transition-colors ${picked === p.start ? "border-[#FF3B30] bg-[#FF3B30]/10" : "border-white/10 hover:border-white/30"}`}
                    data-testid={`proposed-${i}`}
                  >
                    <div className="font-heading text-sm">{new Date(p.start).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="label-eyebrow">SHOP'S OPEN SLOTS</div>
              <select value={days} onChange={(e) => setDays(parseInt(e.target.value))} className="input-base w-32 py-1 text-xs">
                <option value={7}>Next 7 days</option>
                <option value={14}>Next 14 days</option>
                <option value={30}>Next 30 days</option>
              </select>
            </div>
            {loading ? (
              <div className="text-sm text-zinc-500">Loading availability...</div>
            ) : Object.keys(grouped).length === 0 ? (
              <div className="text-sm text-zinc-500 py-6 text-center border border-dashed border-white/10">No open slots in this window. Try a wider range or ask the shop to update their schedule.</div>
            ) : (
              <div className="space-y-3 max-h-[280px] overflow-y-auto pr-1">
                {Object.entries(grouped).map(([d, list]) => (
                  <div key={d}>
                    <div className="label-eyebrow text-zinc-500 mb-2">{d}</div>
                    <div className="flex flex-wrap gap-2">
                      {list.map((s) => (
                        <button
                          key={s.start}
                          type="button"
                          onClick={() => setPicked(s.start)}
                          className={`border px-3 py-1.5 text-xs font-mono transition-colors ${picked === s.start ? "border-[#FF3B30] bg-[#FF3B30]/10 text-white" : "border-white/10 text-zinc-300 hover:border-white/30"}`}
                          data-testid={`slot-${s.start}`}
                        >
                          {new Date(s.start).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {meta && <div className="text-[10px] text-zinc-600 mt-2 font-mono">{meta.slot_minutes}-min slots · {meta.timezone}</div>}
          </div>

          <div>
            <label className="label-eyebrow block mb-2">NOTES (OPTIONAL)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="input-base" placeholder="Anything the shop should know" data-testid="booking-notes" />
          </div>
        </div>

        <DialogFooter>
          <button onClick={submit} disabled={submitting || !picked} className="btn-primary" data-testid="booking-confirm-btn">{submitting ? "..." : picked ? `Book ${new Date(picked).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : "Pick a time"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const ISSUE_CATEGORIES = ["Engine", "Transmission", "Brakes", "Electrical", "Suspension", "Body", "Tires", "HVAC", "Exhaust", "Other"];

function EditIssueDialog({ open, onOpenChange, issue, pendingBidCount, onSuccess }) {
  const [form, setForm] = useState(() => ({
    title: "",
    description: "",
    category: "Other",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_year: "",
    location: "",
  }));
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Hydrate the form whenever the dialog opens with the current issue values.
  useEffect(() => {
    if (open && issue) {
      setForm({
        title: issue.title || "",
        description: issue.description || "",
        category: issue.category || issue.ai_category || "Other",
        vehicle_make: issue.vehicle_make || "",
        vehicle_model: issue.vehicle_model || "",
        vehicle_year: issue.vehicle_year || "",
        location: issue.location || "",
      });
    }
  }, [open, issue]);

  // Detect whether any "material" field changed from the original (mirrors backend logic).
  const hasMaterialChange = !!issue && (
    form.title.trim() !== (issue.title || "") ||
    form.description.trim() !== (issue.description || "") ||
    form.category !== (issue.category || issue.ai_category || "Other") ||
    (form.vehicle_make || "") !== (issue.vehicle_make || "") ||
    (form.vehicle_model || "") !== (issue.vehicle_model || "") ||
    String(form.vehicle_year || "") !== String(issue.vehicle_year || "") ||
    (form.location || "") !== (issue.location || "")
  );
  const willVoidQuotes = hasMaterialChange && pendingBidCount > 0;

  const persist = async () => {
    setSaving(true);
    try {
      const patch = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        vehicle_make: form.vehicle_make || null,
        vehicle_model: form.vehicle_model || null,
        vehicle_year: form.vehicle_year ? Number(form.vehicle_year) : null,
        location: form.location || null,
      };
      const res = await api.patch(`/issues/${issue.issue_id}`, patch);
      const voided = res.data?.voided_bid_count || 0;
      if (voided > 0) {
        toast.success(`Issue updated · ${voided} quote${voided === 1 ? "" : "s"} voided, shops notified.`);
      } else {
        toast.success("Issue updated");
      }
      setConfirmOpen(false);
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save changes");
    } finally {
      setSaving(false);
    }
  };

  const onSaveClicked = () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Title and description are required");
      return;
    }
    if (willVoidQuotes) {
      setConfirmOpen(true);
    } else {
      persist();
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-[#121214] border-white/10 text-white rounded-none max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-issue-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Edit issue</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="label-eyebrow block mb-2">TITLE *</label>
              <input
                className="input-base"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Brakes squeaking when stopping"
                data-testid="edit-title-input"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">DESCRIPTION *</label>
              <textarea
                className="input-base"
                rows={4}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Describe the problem in detail..."
                data-testid="edit-description-input"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">CATEGORY *</label>
              <select
                className="input-base"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="edit-category-input"
              >
                {ISSUE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow block mb-2">MAKE</label>
                <input
                  className="input-base"
                  value={form.vehicle_make}
                  onChange={(e) => setForm({ ...form, vehicle_make: e.target.value })}
                  placeholder="Toyota"
                  data-testid="edit-make-input"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">MODEL</label>
                <input
                  className="input-base"
                  value={form.vehicle_model}
                  onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })}
                  placeholder="Camry"
                  data-testid="edit-model-input"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">YEAR</label>
                <input
                  type="number"
                  className="input-base"
                  value={form.vehicle_year}
                  onChange={(e) => setForm({ ...form, vehicle_year: e.target.value })}
                  placeholder="2011"
                  data-testid="edit-year-input"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">ZIP</label>
                <input
                  className="input-base"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="92691"
                  data-testid="edit-zip-input"
                />
              </div>
            </div>
            {willVoidQuotes && (
              <div className="border border-[#FF9500]/40 bg-[#FF9500]/10 px-4 py-3 text-xs" data-testid="edit-void-warning">
                <div className="text-[#FF9500] font-bold uppercase tracking-widest mb-1">Heads up</div>
                <p className="text-zinc-200">
                  You have <strong>{pendingBidCount}</strong> pending quote{pendingBidCount === 1 ? "" : "s"} on this issue. Changing the scope (title, description, category, vehicle, or location) will <strong>void</strong> {pendingBidCount === 1 ? "it" : "them"} and the shop{pendingBidCount === 1 ? "" : "s"} will need to send a fresh quote.
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 flex-col-reverse sm:flex-row">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              className="btn-secondary"
              data-testid="edit-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSaveClicked}
              disabled={saving}
              className="btn-primary"
              data-testid="edit-save-btn"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Secondary "are you sure" confirm — only shown when changes will void existing quotes. */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-[#121214] border-white/10 text-white rounded-none max-w-md" data-testid="edit-confirm-void-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Void {pendingBidCount} quote{pendingBidCount === 1 ? "" : "s"}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <p className="text-zinc-300">
              Saving these changes will <strong>void</strong> the {pendingBidCount} pending quote{pendingBidCount === 1 ? "" : "s"} on this job. The shop{pendingBidCount === 1 ? "" : "s"} who already quoted will be notified and can send a fresh quote based on the updated details.
            </p>
            <p className="text-zinc-500 text-xs italic">
              Tip: small typo fixes count as "scope changes" too. If you only want to tweak wording, that's still treated as new information for shops.
            </p>
          </div>
          <DialogFooter className="gap-2 flex-col-reverse sm:flex-row">
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              disabled={saving}
              className="btn-secondary"
              data-testid="edit-confirm-cancel-btn"
            >
              Keep editing
            </button>
            <button
              type="button"
              onClick={persist}
              disabled={saving}
              className="btn-primary"
              data-testid="edit-confirm-void-btn"
            >
              {saving ? "Saving…" : "Save & void quotes"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
